import { Field, Button, Input } from '@giyus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthLayout } from '@giyus/patterns';
import { useAuth } from '../lib/auth/authContext';
import { useAppEnv } from '../lib/appEnvContext';

export const ENTRA_TENANT_ID = '5fd3d205-ca34-4bb3-add4-ffa87820598d';
export const ENTRA_CLIENT_ID = 'c38d3756-f7bc-4a23-b312-c2a677e1036d';
export const GIYUS_ENTRA_DOMAIN = '@giyus.com.br';
export const ENTRA_AUTHORIZE_ENDPOINT = `https://login.microsoftonline.com/${ENTRA_TENANT_ID}/oauth2/v2.0/authorize`;
export const ENTRA_TOKEN_ENDPOINT = `https://login.microsoftonline.com/${ENTRA_TENANT_ID}/oauth2/v2.0/token`;

export function isGiyusEntraEmail(email: string): boolean {
  return email.trim().toLowerCase().endsWith(GIYUS_ENTRA_DOMAIN);
}

const KNOWN_USERS: Readonly<
  Record<
    string,
    {
      readonly email: string;
      readonly displayName: string;
      readonly isHoldingEntra: boolean;
      readonly isSuperUser?: boolean;
      readonly roleLabel: string;
    }
  >
> = {
  'syllas@giyus.com.br': {
    email: 'syllas@giyus.com.br',
    displayName: 'Syllas Souza (Usuário Master / Root)',
    isHoldingEntra: true,
    isSuperUser: true,
    roleLabel: 'ROOT / MASTER · SUPER_USER da Plataforma GIYUS',
  },
  syllas: {
    email: 'syllas@giyus.com.br',
    displayName: 'Syllas Souza (Usuário Master / Root)',
    isHoldingEntra: true,
    isSuperUser: true,
    roleLabel: 'ROOT / MASTER · SUPER_USER da Plataforma GIYUS',
  },
};

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function startMicrosoftEntraOidcRedirect(loginHint?: string): Promise<void> {
  const trimmedHint = loginHint?.trim();
  if (trimmedHint && !isGiyusEntraEmail(trimmedHint)) {
    throw new Error('Todos os usuários do Microsoft Entra ID devem pertencer ao domínio @giyus.com.br.');
  }

  const randomBytes = new Uint8Array(32);
  window.crypto.getRandomValues(randomBytes);
  const codeVerifier = toBase64Url(randomBytes);
  const digest = await window.crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(codeVerifier),
  );
  const codeChallenge = toBase64Url(new Uint8Array(digest));
  const stateBytes = new Uint8Array(16);
  window.crypto.getRandomValues(stateBytes);
  const state = `entra_oidc_${toBase64Url(stateBytes)}`;
  const redirectUri = `${window.location.origin}/auth/callback`;

  try {
    window.sessionStorage.setItem('giyus.entra.pkce_verifier', codeVerifier);
    window.sessionStorage.setItem('giyus.entra.oauth_state', state);
    window.sessionStorage.setItem('giyus.entra.redirect_uri', redirectUri);
  } catch {
    // Ignore storage quota errors
  }

  const params = new URLSearchParams({
    client_id: ENTRA_CLIENT_ID,
    response_type: 'code',
    redirect_uri: redirectUri,
    response_mode: 'query',
    scope: 'openid profile email User.Read Mail.Read Mail.Read.Shared',
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
    domain_hint: 'giyus.com.br',
    state,
  });
  if (trimmedHint) {
    params.set('login_hint', trimmedHint.toLowerCase());
  }

  window.location.assign(`${ENTRA_AUTHORIZE_ENDPOINT}?${params.toString()}`);
}

export function authenticatePortalAccount(
  rawInput: string,
  rawPassword: string,
  displayNameOverride?: string,
): {
  readonly ok: boolean;
  readonly error?: string;
  readonly redirectTo?: string;
} {
  const identifier = rawInput.trim().toLowerCase();
  const pw = rawPassword.trim();
  if (!identifier) {
    return {
      ok: false,
      error:
        'Informe seu e-mail da Holding (@giyus.com.br via Microsoft Entra) ou seu e-mail externo autorizado.',
    };
  }
  if (pw === 'entra_oidc_verified' && !isGiyusEntraEmail(identifier)) {
    return {
      ok: false,
      error: 'Acesso restrito: todos os usuários do Microsoft Entra ID devem ser @giyus.com.br.',
    };
  }
  if (
    !pw ||
    ['wrong', 'invalid', 'incorrect', 'bad_password', 'senha_errada'].includes(pw.toLowerCase())
  ) {
    return {
      ok: false,
      error: 'Credenciais inválidas no Microsoft Entra ID / Portal de Acesso.',
    };
  }
  if (pw !== 'pass' && pw !== 'entra_oidc_verified' && pw.length < 4) {
    return {
      ok: false,
      error: 'A senha do Microsoft Entra / Portal deve possuir ao menos 4 caracteres.',
    };
  }

  const known = KNOWN_USERS[identifier];
  if (!known && pw !== 'entra_oidc_verified' && !isGiyusEntraEmail(identifier)) {
    return {
      ok: false,
      error: 'Acesso restrito: somente contas autorizadas (@giyus.com.br) estão habilitadas.',
    };
  }
  const resolvedEmail = known?.email ?? identifier;
  const resolvedName =
    displayNameOverride ?? known?.displayName ?? resolvedEmail.split('@')[0] ?? resolvedEmail;

  try {
    window.sessionStorage.setItem('giyus.auth.login_email', resolvedEmail);
    window.sessionStorage.setItem('giyus.auth.login_name', resolvedName);
    window.sessionStorage.removeItem('giyus.auth.login_override_external');
    window.sessionStorage.setItem('giyus.fake.scenario', 'syllas-admin');
    return { ok: true, redirectTo: '/network' };
  } catch {
    return { ok: true, redirectTo: '/network' };
  }
}

export const LOGIN_ERROR_STORAGE_KEY = 'giyus.auth.login_error';

function consumeStoredLoginError(): string | null {
  try {
    if (typeof window === 'undefined') {
      return null;
    }
    const stored = window.sessionStorage.getItem(LOGIN_ERROR_STORAGE_KEY);
    if (stored) {
      window.sessionStorage.removeItem(LOGIN_ERROR_STORAGE_KEY);
      return stored;
    }
  } catch {
    // Ignore storage errors
  }
  return null;
}

export function EntraAccessPortal({
  statusLabel,
  initialError,
  onClose,
}: {
  readonly statusLabel?: string;
  readonly initialError?: string;
  readonly onClose?: () => void;
}) {
  const { t } = useTranslation(['shell', 'common']);
  const { backend, refreshMe, signIn } = useAuth();
  const env = useAppEnv();
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(
    () => consumeStoredLoginError() ?? initialError ?? null,
  );
  const [redirectingEntra, setRedirectingEntra] = useState(false);
  const [submittingLocal, setSubmittingLocal] = useState(false);

  const handleLogin = async () => {
    setErrorMessage(null);
    if (env.authMode === 'fake' && env.environment === 'dev') {
      const result = authenticatePortalAccount(emailInput, passwordInput);
      if (!result.ok) {
        setErrorMessage(result.error ?? t('shell:login.error'));
        return;
      }
      window.location.href = result.redirectTo ?? '/network';
      return;
    }

    setSubmittingLocal(true);
    try {
      const apiBase = env.apiBaseUrl.replace(/\/$/, '');
      const response = await fetch(`${apiBase}/connect/local`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: emailInput.trim(),
          password: passwordInput,
        }),
      });
      if (!response.ok) {
        setErrorMessage(t('shell:login.error'));
        return;
      }
      const data = (await response.json()) as {
        readonly accessToken: string;
        readonly expiresIn?: number;
        readonly email?: string;
        readonly displayName?: string;
      };
      if (backend.setExternalSession) {
        backend.setExternalSession(data.accessToken, data.expiresIn ?? 3600);
      } else {
        await signIn();
        return;
      }
      try {
        if (data.email) {
          window.sessionStorage.setItem('giyus.auth.login_email', data.email);
        }
        if (data.displayName) {
          window.sessionStorage.setItem('giyus.auth.login_name', data.displayName);
        }
      } catch {
        // Ignore storage errors
      }
      await refreshMe();
      window.location.href = '/network';
    } catch {
      setErrorMessage(t('shell:login.error'));
    } finally {
      setSubmittingLocal(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-surface" data-testid="auth-status">
      <AuthLayout
        overline={t('shell:login.overline')}
        statement={[
          t('shell:login.statement1'),
          t('shell:login.statement2'),
          t('shell:login.statement3'),
        ]}
        description={t('shell:login.brandDescription')}
        legalName="GIYUS RESEARCH CENTER LTDA · São Paulo/SP"
      >
        {onClose && (
          <Button variant="ghost" className="self-end" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
        )}
        <div data-testid="entra-login-portal">
          <h1 className="text-headline font-semibold text-heading">{t('shell:login.title')}</h1>
          <p className="mt-1.5 text-body text-muted-foreground">{t('shell:login.description')}</p>
        </div>
        {statusLabel && (
          <p className="sr-only" role="status">
            {statusLabel}
          </p>
        )}
        <Button
          className="min-h-11 w-full font-semibold"
          disabled={redirectingEntra || submittingLocal}
          onClick={() => {
            setErrorMessage(null);
            if (emailInput.trim() && !isGiyusEntraEmail(emailInput)) {
              setErrorMessage(
                'Todos os usuários do Microsoft Entra ID devem ser @giyus.com.br. Para outras contas, utilize login e senha local.',
              );
              return;
            }
            setRedirectingEntra(true);
            void startMicrosoftEntraOidcRedirect(emailInput).catch((err: unknown) => {
              setRedirectingEntra(false);
              setErrorMessage(err instanceof Error ? err.message : t('shell:login.error'));
            });
          }}
          data-testid="entra-oidc-login-button"
        >
          <img src="/brands/microsoft.svg" width="14" height="14" alt="" aria-hidden="true" />
          {t(redirectingEntra ? 'shell:auth.signingIn' : 'shell:login.microsoft')}
        </Button>
        <div className="flex items-center gap-3 text-caption text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          {t('shell:login.external')}
          <span className="h-px flex-1 bg-border" />
        </div>
        <form
          className="flex flex-col gap-[22px]"
          onSubmit={(event) => {
            event.preventDefault();
            void handleLogin();
          }}
        >
          <Field label={t('shell:login.email')} id="portal-email">
            {(control) => (
              <Input
                {...control}
                type="email"
                autoComplete="username"
                placeholder="nome@centro.com.br"
                required
                value={emailInput}
                onChange={(event) => setEmailInput(event.target.value)}
                className="min-h-10"
              />
            )}
          </Field>
          <Field label={t('shell:login.password')} id="portal-password">
            {(control) => (
              <Input
                {...control}
                type="password"
                autoComplete="current-password"
                required
                value={passwordInput}
                onChange={(event) => setPasswordInput(event.target.value)}
                className="min-h-10"
              />
            )}
          </Field>
          {errorMessage && (
            <p role="alert" className="text-body text-danger-subtle-foreground">
              {errorMessage}
            </p>
          )}
          <Button
            type="submit"
            variant="outline"
            disabled={submittingLocal || redirectingEntra}
            className="min-h-10 w-full font-semibold text-heading"
          >
            {t(submittingLocal ? 'shell:auth.signingIn' : 'shell:login.title')}
          </Button>
        </form>
        <div className="flex flex-wrap items-center justify-between gap-2 text-body-sm">
          <a
            className="inline-flex min-h-6 items-center text-link"
            href={`${env.oidcAuthority.replace(/\/$/, '')}/account/password/forgot`}
          >
            {t('shell:login.forgot')}
          </a>
          <span className="text-muted-foreground">{t('shell:login.mfa')}</span>
        </div>
      </AuthLayout>
    </div>
  );
}
